import { selectCurrentCampaignId } from 'store/campaigns';
import { selectAllCommittedFlights } from 'store/flights/committed';
import { createSelector } from 'reselect';
import { RootState } from 'store';
import adapter from './committedProposals.adapter';
import { ProposalParams } from '../proposals.types';
import { REDUCER_KEY } from './committedProposals.consts';
import { normalizeDateFromApi } from 'helpers/datetimeHelpers';
import { selectCommittedBasket } from 'store/baskets/committed';
import { calculateTimeBoundaries } from '../proposals.utils';

const selectors = adapter.getSelectors(
  (state: RootState) => state[REDUCER_KEY],
);
export const { selectIds, selectTotal, selectAll } = selectors;

export const selectById = (
  state: RootState,
  id: number,
): ProposalParams | null => selectors.selectById(state, id) || null;

export const selectCurrentCommittedProposalId = (state: RootState): number =>
  state[REDUCER_KEY].current;

export const selectAllProposals = createSelector(
  state => selectors.selectAll(state),
  finalizedProposals => finalizedProposals || [],
);

export const selectCurrentCommittedProposal = createSelector(
  state => selectCurrentCommittedProposalId(state),
  state => selectAll(state),
  (currentProposalId, allProposals) =>
    allProposals.find(({ id }) => id === currentProposalId) || null,
);

export function selectCommittedProposal(
  state: RootState,
  proposalId: number,
): ProposalParams | undefined {
  return selectors.selectById(state, proposalId) || undefined;
}

export const selectProposalsOfCurrentCampaign = createSelector(
  selectAllProposals,
  selectCurrentCampaignId,
  (allProposals, campaignId) =>
    allProposals.filter(proposal => proposal.campaign?.id === campaignId) || [],
);

export const selectCurrentCommittedProposalStatus = createSelector(
  selectCurrentCommittedProposal,
  proposal => proposal?.status,
);

export const selectCommittedProposalStatusById = (
  state: RootState,
  proposalId: number,
): string | undefined => {
  const proposal = selectCommittedProposal(state, proposalId);
  return proposal?.status;
};

export const selectCurrentProposalCampaignDates = createSelector(
  selectCurrentCommittedProposal,
  proposal => {
    if (!proposal) {
      return null;
    }
    return {
      startDate: normalizeDateFromApi(proposal.campaign?.startDate) || null,
      endDate: normalizeDateFromApi(proposal.campaign?.endDate) || null,
    };
  },
);

export const selectCurrentProposalFlightBoundries = createSelector(
  selectCurrentCommittedProposal,
  proposal => {
    if (!proposal) {
      return null;
    }
    return {
      startDate:
        normalizeDateFromApi(proposal.flightTimeBoundaries?.startDate) || null,
      endDate:
        normalizeDateFromApi(proposal.flightTimeBoundaries?.endDate) || null,
    };
  },
);

export const selectCurrentCommittedProposalTimeBoundaries = createSelector(
  selectCurrentCommittedProposal,
  calculateTimeBoundaries,
);

export const selectCurrentProposalPricingModel = createSelector(
  selectCurrentCommittedProposal,
  proposal => proposal?.generalProductValue?.pricingModel,
);

export const selectCommittedProposalsOfCommittedBasket = (
  state: RootState,
  basketId: number,
): ProposalParams[] => {
  const proposals = selectors.selectAll(state);
  const basketProposals =
    selectCommittedBasket(state, basketId)?.proposals || [];

  return proposals.filter(proposal => basketProposals.includes(proposal.id));
};

export const selectCommittedFlightsOfCurrentCommittedProposal = createSelector(
  selectCurrentCommittedProposal,
  selectAllCommittedFlights,
  (proposal, flights) => {
    const flightIds = proposal?.flights || [];
    const flight = flights.filter(flight => flightIds.includes(flight.id));
    return flight || [];
  },
);

export const selectAttachmentsOfCurrentCommittedProposal = createSelector(
  selectCurrentCommittedProposal,
  proposal => proposal?.attachments || [],
);
